import apiClient from './apiClient';
import type {
  DlqMessage,
  DlqMessageDetails,
  DlqAnalysisResponse,
  PagedDlqResponse,
  BatchOperationResult,
  PurgeResult,
  DlqSummaryResponse,
  DlqSampleAnalysisResponse,
  DlqDashboardResponse,
  DlqTrendResponse,
  QueueDlqInfo,
  DlqOperationResponse,
  QueueCountsAggregatedHistoryResponse
} from '../types';

/**
 * Service para interagir com a API de Dead Letter Queue
 */

export const dlqService = {
  /**
   * Lista mensagens na DLQ (método legado)
   */
  async listMessages(maxMessages: number = 100): Promise<DlqMessage[]> {
    const response = await apiClient.get<DlqMessage[]>('/dlq', {
      params: { maxMessages }
    });
    return response.data;
  },

  /**
   * Obtém detalhes completos de uma mensagem DLQ
   */
  async getMessageDetails(sequenceNumber: number): Promise<DlqMessageDetails> {
    const response = await apiClient.get<DlqMessageDetails>(`/dlq/${sequenceNumber}`);
    return response.data;
  },

  /**
   * Reprocessa uma mensagem (move de volta para fila principal)
   */
  async reprocessMessage(sequenceNumber: number): Promise<void> {
    await apiClient.post(`/dlq/${sequenceNumber}/reprocess`);
  },

  /**
   * Reprocessa múltiplas mensagens em lote
   */
  async reprocessBatch(sequenceNumbers: number[]): Promise<void> {
    await apiClient.post('/dlq/reprocess-batch', { sequenceNumbers });
  },

  /**
   * Deleta uma mensagem da DLQ permanentemente
   */
  async deleteMessage(sequenceNumber: number): Promise<void> {
    await apiClient.delete(`/dlq/${sequenceNumber}`);
  },

  // ============================================================================
  // Novos métodos de análise e operações em lote
  // ============================================================================

  /**
   * Analisa todas as mensagens na DLQ e retorna estatísticas
   */
  async getAnalysis(): Promise<DlqAnalysisResponse> {
    const response = await apiClient.get<DlqAnalysisResponse>('/dlq/analysis');
    return response.data;
  },

  /**
   * Lista mensagens com paginação
   */
  async listMessagesPaged(
    pageSize: number = 50,
    fromSequenceNumber?: number,
    messageTypeFilter?: string
  ): Promise<PagedDlqResponse> {
    const response = await apiClient.get<PagedDlqResponse>('/dlq/paged', {
      params: {
        pageSize,
        fromSequenceNumber,
        messageTypeFilter
      }
    });
    return response.data;
  },

  /**
   * Reprocessa todas as mensagens de um tipo específico
   */
  async reprocessByMessageType(messageType: string): Promise<BatchOperationResult> {
    const response = await apiClient.post<BatchOperationResult>(
      `/dlq/reprocess-by-type/${encodeURIComponent(messageType)}`
    );
    return response.data;
  },

  /**
   * Deleta todas as mensagens com um padrão de erro específico
   */
  async deleteByErrorPattern(errorPattern: string): Promise<BatchOperationResult> {
    const response = await apiClient.post<BatchOperationResult>('/dlq/delete-by-pattern', {
      errorPattern
    });
    return response.data;
  },

  /**
   * Remove TODAS as mensagens da DLQ (use com cuidado!)
   */
  async purgeAll(): Promise<PurgeResult> {
    const response = await apiClient.delete<PurgeResult>('/dlq/purge');
    return response.data;
  },

  // ============================================================================
  // Novos métodos otimizados (rápidos - usam métricas)
  // ============================================================================

  /**
   * [RÁPIDO] Obtém resumo da DLQ usando métricas do Service Bus (não lê mensagens)
   */
  async getSummary(): Promise<DlqSummaryResponse> {
    const response = await apiClient.get<DlqSummaryResponse>('/dlq/summary');
    return response.data;
  },

  /**
   * [RÁPIDO] Histórico agregado de DLQ e Scheduled para gráficos em tempo real
   * Retorna: histórico por hora (últimas 23h) + dados granulares da hora atual
   */
  async getAggregatedHistory(): Promise<QueueCountsAggregatedHistoryResponse> {
    const response = await apiClient.get<QueueCountsAggregatedHistoryResponse>('/dlq/history/aggregated');
    return response.data;
  },

  /**
   * [RÁPIDO] Análise por amostragem (peek de N mensagens para categorização)
   */
  async getSampleAnalysis(sampleSize: number = 100): Promise<DlqSampleAnalysisResponse> {
    const response = await apiClient.get<DlqSampleAnalysisResponse>('/dlq/sample-analysis', {
      params: { sampleSize: Math.min(sampleSize, 200) }
    });
    return response.data;
  },

  /**
   * [RÁPIDO] Dashboard completo combinando métricas e amostragem
   */
  async getDashboard(
    sampleSize: number = 100,
    includeTrend: boolean = false,
    trendHours: number = 24
  ): Promise<DlqDashboardResponse> {
    const response = await apiClient.get<DlqDashboardResponse>('/dlq/dashboard', {
      params: { sampleSize, includeTrend, trendHours }
    });
    return response.data;
  },

  /**
   * [RÁPIDO] Lista todas as filas do namespace com informações de DLQ
   */
  async getAllQueues(): Promise<QueueDlqInfo[]> {
    const response = await apiClient.get<QueueDlqInfo[]>('/dlq/queues');
    return response.data;
  },

  /**
   * [OPCIONAL] Tendência histórica da DLQ
   */
  async getTrend(hours: number = 24): Promise<DlqTrendResponse> {
    const response = await apiClient.get<DlqTrendResponse>('/dlq/trend', {
      params: { hours }
    });
    return response.data;
  },

  // ============================================================================
  // Operações Assíncronas (com Polling)
  // ============================================================================

  /**
   * [ASSÍNCRONO] Inicia operação de delete por padrão de erro
   * Retorna imediatamente com operationId para polling
   */
  async startDeleteByPatternAsync(errorPattern: string): Promise<DlqOperationResponse> {
    const response = await apiClient.post<DlqOperationResponse>('/dlq/operations/delete-by-pattern', {
      errorPattern
    });
    return response.data;
  },

  /**
   * [ASSÍNCRONO] Inicia operação de reprocess por MessageType
   * Retorna imediatamente com operationId para polling
   */
  async startReprocessByTypeAsync(messageType: string): Promise<DlqOperationResponse> {
    const response = await apiClient.post<DlqOperationResponse>('/dlq/operations/reprocess-by-type', {
      messageType
    });
    return response.data;
  },

  /**
   * [ASSÍNCRONO] Inicia operação de purge (remove TODAS as mensagens)
   * Retorna imediatamente com operationId para polling
   */
  async startPurgeAsync(): Promise<DlqOperationResponse> {
    const response = await apiClient.post<DlqOperationResponse>('/dlq/operations/purge');
    return response.data;
  },

  /**
   * Obtém status de uma operação em andamento
   */
  async getOperationStatus(operationId: string): Promise<DlqOperationResponse> {
    const response = await apiClient.get<DlqOperationResponse>(`/dlq/operations/${operationId}`);
    return response.data;
  },

  /**
   * Lista operações recentes
   */
  async getRecentOperations(limit: number = 20): Promise<DlqOperationResponse[]> {
    const response = await apiClient.get<DlqOperationResponse[]>('/dlq/operations', {
      params: { limit }
    });
    return response.data;
  },

  /**
   * Cancela uma operação em andamento
   */
  async cancelOperation(operationId: string): Promise<void> {
    await apiClient.post(`/dlq/operations/${operationId}/cancel`);
  },

  /**
   * Polling helper - aguarda até que a operação seja concluída
   * Chama callback com status atualizado a cada intervalo
   */
  async pollOperation(
    operationId: string,
    onUpdate: (operation: DlqOperationResponse) => void,
    intervalMs: number = 2000,
    maxAttempts: number = 300 // 10 minutos com 2s de intervalo
  ): Promise<DlqOperationResponse> {
    let attempts = 0;

    while (attempts < maxAttempts) {
      const operation = await this.getOperationStatus(operationId);
      onUpdate(operation);

      if (operation.status === 'Completed' || operation.status === 'Failed' || operation.status === 'Cancelled') {
        return operation;
      }

      await new Promise(resolve => setTimeout(resolve, intervalMs));
      attempts++;
    }

    throw new Error('Operation timeout - polling exceeded max attempts');
  }
};
